import { useState } from "react";
import { Menu, X, ArrowRight, ArrowUpRight, Phone } from "lucide-react";
import { NetworkCanvas } from "../components/NetworkCanvas";
import { KeralaMap } from "../components/KeralaMap";
import { EnquiryForm, ContactAside } from "../components/EnquiryForm";
import { NAV_ITEMS, CAPABILITIES, SOLUTIONS, INDUSTRIES, DISTRICTS, CONTACT, LOGO } from "../data/content";

const img = (id, w = 1200) => `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=70`;

const SPACES = [
  { name: "Secure Buildings", text: "Surveillance, access control and monitoring for offices, institutions and industry.", img: "photo-1557862921-37829c790f19" },
  { name: "Connected Workplaces", text: "Enterprise networks, Wi-Fi and communication systems that people rely on daily.", img: "photo-1497366811353-6870744d04b2" },
  { name: "Intelligent Industry", text: "Automation, power continuity and data infrastructure for industrial environments.", img: "photo-1558494949-ef010cbdcc31" },
  { name: "Smart Homes & Communities", text: "Home and community automation designed around the people who live there.", img: "photo-1512917774080-9991f1c4c750" },
];

const Header = () => {
  const [open, setOpen] = useState(false);
  return (
    <header className="absolute top-0 inset-x-0 z-40">
      <div className="nx-wrap flex items-center justify-between h-16 sm:h-20">
        <a href="#top" className="flex items-center gap-3" data-testid="c3-logo">
          <img src={LOGO.iconDark} alt="NexSecure emblem" className="h-9 sm:h-11 w-auto" />
          <img src={LOGO.wordDark} alt="NexSecure Technologies" className="h-3.5 sm:h-4 w-auto" />
        </a>
        <nav className="hidden lg:flex items-center gap-7" data-testid="c3-nav">
          {NAV_ITEMS.map((n) => (
            <a key={n} href="#solutions" className="text-[11px] tracking-[0.22em] uppercase text-white/70 hover:text-nx-goldlight transition-colors">{n}</a>
          ))}
        </nav>
        <div className="flex items-center gap-3">
          <a href="#enquiry" data-testid="c3-header-cta" className="hidden sm:inline-block bg-nx-gold hover:bg-nx-goldlight text-nx-navy text-xs font-semibold tracking-[0.14em] uppercase px-5 py-3 rounded-full transition-colors">Request a Site Assessment</a>
          <button className="lg:hidden text-white p-2" onClick={() => setOpen(!open)} data-testid="c3-mobile-menu-button" aria-label="Menu">{open ? <X size={22} /> : <Menu size={22} />}</button>
        </div>
      </div>
      {open && (
        <nav className="lg:hidden mx-4 rounded-2xl bg-nx-navy/95 backdrop-blur-md px-6 py-4 flex flex-col gap-4" data-testid="c3-mobile-nav">
          {NAV_ITEMS.map((n) => <a key={n} href="#solutions" onClick={() => setOpen(false)} className="text-xs tracking-[0.22em] uppercase text-white/80">{n}</a>)}
          <a href="#enquiry" onClick={() => setOpen(false)} className="text-nx-goldlight text-xs tracking-[0.22em] uppercase font-semibold">Request a Site Assessment</a>
        </nav>
      )}
    </header>
  );
};

const Hero = () => (
  <section id="top" className="relative min-h-screen flex items-center overflow-hidden" data-testid="c3-hero">
    <img src={img("photo-1497366216548-37526070297c", 1800)} alt="Modern connected workspace interior" className="absolute inset-0 w-full h-full object-cover" />
    <div className="absolute inset-0 bg-gradient-to-b from-nx-navy/90 via-nx-navy/75 to-nx-navy/95" />
    <NetworkCanvas skyline={false} className="absolute inset-0 w-full h-full opacity-50" />
    <div className="relative nx-wrap pt-32 pb-24 w-full">
      <div className="max-w-2xl">
        <div className="flex items-center gap-4 mb-7" data-testid="c3-hero-eyebrow">
          <span className="h-px w-12 bg-nx-goldlight" />
          <span className="text-[11px] sm:text-xs tracking-[0.3em] uppercase text-nx-goldlight">Technology Infrastructure & Integrated Solutions</span>
        </div>
        <h1 className="font-outfit text-4xl sm:text-5xl lg:text-6xl text-white leading-[1.08]" style={{ fontWeight: 600 }} data-testid="c3-hero-title">
          Engineering <span className="text-nx-goldlight">Intelligent</span> Infrastructure
        </h1>
        <p className="mt-7 text-base md:text-lg text-white/75 leading-relaxed" data-testid="c3-hero-subtitle">
          Secure buildings, connected workplaces, intelligent industry and smart communities — designed and cared for by one trusted Kerala partner.
        </p>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <a href="#enquiry" data-testid="c3-hero-primary-cta" className="group inline-flex items-center justify-center gap-3 bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold text-sm px-9 py-4 rounded-full transition-all duration-300 hover:-translate-y-0.5 hover:shadow-xl hover:shadow-nx-gold/25">
            Request a Site Assessment <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a href="#solutions" data-testid="c3-hero-secondary-cta" className="inline-flex items-center justify-center border border-white/30 hover:border-nx-goldlight text-white text-sm px-9 py-4 rounded-full backdrop-blur-sm transition-colors duration-300">Explore Solutions</a>
        </div>
        <div className="mt-12 flex flex-wrap gap-3" data-testid="c3-hero-capabilities">
          {CAPABILITIES.map((c) => (
            <span key={c.title} className="text-[11px] tracking-[0.16em] uppercase text-white/70 border border-white/20 rounded-full px-4 py-2 backdrop-blur-sm">{c.title}</span>
          ))}
        </div>
      </div>
    </div>
  </section>
);

const Spaces = () => (
  <section id="solutions" className="bg-nx-cream py-20 sm:py-28" data-testid="c3-spaces">
    <div className="nx-wrap">
      <div className="max-w-2xl mb-14">
        <span className="text-[11px] tracking-[0.3em] uppercase text-nx-gold font-semibold">Intelligent Spaces</span>
        <h2 className="font-outfit text-3xl sm:text-4xl text-nx-ink mt-4" style={{ fontWeight: 600 }}>Technology that belongs to the place — and the people in it.</h2>
        <p className="text-base text-neutral-500 mt-5 leading-relaxed">From corporate offices to residential communities, we engineer security, connectivity and automation that feels natural, not intrusive.</p>
      </div>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
        {SPACES.map((s, i) => (
          <a key={s.name} href="#enquiry" className="group relative rounded-3xl overflow-hidden h-80 sm:h-96 block" data-testid={`c3-space-${i}`}>
            <img src={img(s.img, 800)} alt={s.name} className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-nx-navy/95 via-nx-navy/30 to-transparent" />
            <div className="absolute inset-x-0 bottom-0 p-6">
              <h3 className="font-outfit text-white text-lg" style={{ fontWeight: 600 }}>{s.name}</h3>
              <p className="text-sm text-white/70 mt-2 leading-relaxed">{s.text}</p>
              <span className="mt-4 inline-flex items-center gap-2 text-[11px] tracking-[0.2em] uppercase text-nx-goldlight">Explore <ArrowUpRight size={14} /></span>
            </div>
          </a>
        ))}
      </div>
      <div className="mt-10 flex flex-wrap gap-2" data-testid="c3-solution-chips">
        {SOLUTIONS.map((s) => (
          <span key={s.name} className="text-xs text-neutral-600 bg-white border border-neutral-200 rounded-full px-4 py-2">{s.name}</span>
        ))}
      </div>
    </div>
  </section>
);

const Coverage = () => (
  <section className="relative py-20 sm:py-28 overflow-hidden" data-testid="c3-coverage">
    <img src={img("photo-1504307651254-35680f356dfd", 1600)} alt="On-site engineering work" className="absolute inset-0 w-full h-full object-cover" loading="lazy" />
    <div className="absolute inset-0 bg-nx-navy/90" />
    <div className="relative nx-wrap grid lg:grid-cols-[1fr_1.3fr] gap-14 items-center">
      <div className="flex justify-center">
        <div className="bg-white/[0.04] backdrop-blur-md border border-white/10 rounded-3xl p-8 sm:p-10">
          <KeralaMap tone="dark" className="h-[340px] sm:h-[420px] w-auto" />
        </div>
      </div>
      <div>
        <span className="text-[11px] tracking-[0.3em] uppercase text-nx-goldlight">Kerala Service Coverage</span>
        <h2 className="font-outfit text-3xl sm:text-4xl text-white mt-4" style={{ fontWeight: 600 }}>We come to you — anywhere in Kerala.</h2>
        <p className="text-base text-white/70 mt-5 leading-relaxed">
          On-site technology assessments for businesses, institutions, industries, government organisations and communities across all fourteen districts.
        </p>
        <div className="mt-7 flex flex-wrap gap-2" data-testid="c3-district-list">
          {DISTRICTS.map((d) => (
            <span key={d} className="text-[11px] tracking-wider uppercase text-white/80 border border-white/20 rounded-full px-3.5 py-1.5 backdrop-blur-sm">{d}</span>
          ))}
        </div>
        <div className="mt-10 flex flex-col sm:flex-row gap-4">
          <a href="#enquiry" data-testid="c3-coverage-primary-cta" className="inline-flex items-center justify-center gap-3 bg-nx-gold hover:bg-nx-goldlight text-nx-navy font-semibold text-sm px-9 py-4 rounded-full transition-all duration-300 hover:-translate-y-0.5">
            Request a Site Assessment <ArrowRight size={16} />
          </a>
          <a href="#solutions" data-testid="c3-coverage-secondary-cta" className="inline-flex items-center justify-center border border-white/30 hover:border-nx-goldlight text-white text-sm px-9 py-4 rounded-full transition-colors">Explore Solutions</a>
        </div>
        <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} data-testid="c3-coverage-phone" className="mt-6 inline-flex items-center gap-2 text-white/80 hover:text-nx-goldlight text-sm transition-colors">
          <Phone size={15} className="text-nx-goldlight" /> Prefer to talk? {CONTACT.phone}
        </a>
      </div>
    </div>
  </section>
);

const Enquiry = () => (
  <section id="enquiry" className="bg-nx-cream py-20 sm:py-28" data-testid="c3-enquiry">
    <div className="nx-wrap grid lg:grid-cols-[1.6fr_1fr] gap-10">
      <div className="bg-white rounded-3xl shadow-xl shadow-neutral-200/60 p-7 sm:p-12">
        <span className="text-[11px] tracking-[0.3em] uppercase text-nx-gold font-semibold">Start a Conversation</span>
        <h2 className="font-outfit text-3xl sm:text-4xl text-nx-ink mt-4 mb-3" style={{ fontWeight: 600 }}>Request a consultation.</h2>
        <p className="text-base text-neutral-500 mb-10 max-w-lg leading-relaxed">Choose your solution area, write a few lines about your project — we'll take it from there.</p>
        <EnquiryForm tone="light" source="concept-3" buttonLabel="Send Enquiry" />
      </div>
      <div className="bg-nx-navy rounded-3xl p-8 sm:p-10 lg:mt-24 h-fit">
        <p className="text-[10px] tracking-[0.28em] uppercase text-slate-400 mb-6">Prefer to reach us directly?</p>
        <ContactAside tone="dark" />
      </div>
    </div>
  </section>
);

const Footer = () => (
  <footer className="bg-nx-navy border-t border-white/10 py-12 pb-28" data-testid="c3-footer">
    <div className="nx-wrap flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
      <div className="flex items-center gap-3">
        <img src={LOGO.iconDark} alt="" className="h-10 w-auto" />
        <img src={LOGO.wordDark} alt="NexSecure Technologies" className="h-4 w-auto" />
      </div>
      <nav className="flex flex-wrap gap-x-6 gap-y-2">
        {NAV_ITEMS.map((n) => <a key={n} href="#top" className="text-[11px] tracking-[0.2em] uppercase text-slate-400 hover:text-nx-goldlight transition-colors">{n}</a>)}
      </nav>
      <p className="text-xs text-slate-500">Ernakulam, Kerala · Engineering Intelligent Infrastructure</p>
    </div>
  </footer>
);

export default function ConceptThree() {
  return (
    <div className="font-outfit bg-nx-cream text-nx-ink antialiased">
      <Header />
      <Hero />
      <Spaces />
      <Coverage />
      <Enquiry />
      <Footer />
    </div>
  );
}
